// @ts-nocheck
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Download, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

interface ScoutPayout {
  id: string;
  scout_name: string;
  email: string;
  active_athletes: number;
  total_commissions: number;
  commission_rate: number;
}

const ScoutPayoutsTab: React.FC = () => {
  const { data: scouts, isLoading } = useQuery({
    queryKey: ['scout-payouts'],
    queryFn: async () => {
      // Fetch scouts and relationships in parallel
      const [scoutsResult, relationshipsResult, assignmentsResult] = await Promise.all([
        supabase.from('profiles').select('id, name, email, user_id').eq('role', 'scout'),
        supabase.from('scout_athlete_relationships').select('scout_id, id, status').eq('status', 'active'),
        supabase.from('scout_membership_assignments').select('scout_id, membership_id')
      ]);

      if (scoutsResult.error) throw scoutsResult.error;

      const relationships = relationshipsResult.data || [];
      const assignments = assignmentsResult.data || [];

      // Group by scout_id
      const relationshipsByScout: Record<string, number> = {};
      relationships.forEach(rel => {
        relationshipsByScout[rel.scout_id] = (relationshipsByScout[rel.scout_id] || 0) + 1;
      });

      const assignmentsByScout: Record<string, number> = {};
      assignments.forEach(asn => {
        assignmentsByScout[asn.scout_id] = (assignmentsByScout[asn.scout_id] || 0) + 1;
      });

      const scoutsWithStats = (scoutsResult.data || []).map(scout => {
        const activeAthletes = relationshipsByScout[scout.id] || 0;
        const assignmentCount = assignmentsByScout[scout.id] || 0;
        const estimatedCommissions = activeAthletes * 50; // $50 per active athlete
        const avgCommissionRate = assignmentCount > 0 ? 10 : 0; // Default 10% commission rate

        return {
          id: scout.id,
          scout_name: scout.name || 'Unknown',
          email: scout.email || 'No email',
          active_athletes: activeAthletes,
          total_commissions: estimatedCommissions,
          commission_rate: avgCommissionRate,
        };
      });

      return scoutsWithStats.filter(s => s.total_commissions > 0);
    },
  });

  const exportToCSV = () => {
    if (!scouts) return;

    const headers = ['Scout Name', 'Email', 'Active Athletes', 'Commissions', 'Commission Rate'];
    const rows = scouts.map(s => [
      s.scout_name,
      s.email,
      s.active_athletes,
      `$${s.total_commissions.toFixed(2)}`,
      `${s.commission_rate.toFixed(1)}%`,
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scout-payouts-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('Exported to CSV');
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading scout payouts...</div>;
  }

  const totalPending = scouts?.reduce((sum, s) => sum + s.total_commissions, 0) || 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Commissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalPending)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Active Scouts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{scouts?.length || 0}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Athletes Monitored</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {scouts?.reduce((sum, s) => sum + s.active_athletes, 0) || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Scout Payouts Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle>Scout Commissions</CardTitle>
              <CardDescription>Manage scout commission payouts</CardDescription>
            </div>
            <Button variant="outline" onClick={exportToCSV}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Scout</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Active Athletes</TableHead>
                <TableHead className="text-right">Commission Rate</TableHead>
                <TableHead className="text-right">Total Commissions</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {scouts?.map((scout) => (
                <TableRow key={scout.id}>
                  <TableCell className="font-medium">{scout.scout_name}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{scout.email}</TableCell>
                  <TableCell className="text-right">{scout.active_athletes}</TableCell>
                  <TableCell className="text-right">{scout.commission_rate.toFixed(1)}%</TableCell>
                  <TableCell className="text-right font-semibold">{formatCurrency(scout.total_commissions)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => toast.info('Sending email...')}>
                        <Mail className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => toast.success('Marked as paid')}>
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default ScoutPayoutsTab;
